/**
 * Phase 45 Plan 45-05 — Manager Overview portfolio API client.
 *
 * Typed wrapper around `GET /api/manager/portfolio` (Wave 2 commit f8308a2c).
 * Mirrors the backend's `PortfolioResponse` shape from
 * `backend/src/services/portfolioOverviewService.ts` so consumers compile
 * against the same contract the route schema enforces at runtime.
 *
 * Auth: relies on the existing `auth_token` cookie / Bearer header that
 * `apiGet` (services/api/client.ts) attaches; the route preHandler chain
 * (`authenticate, requireManager`) at the backend rejects anonymous /
 * member calls with 401/403 — handled upstream by the React Router
 * `<ManagerRoute>` guard so this client never sees them in practice.
 *
 * `period === 'custom'` MUST be paired with ISO `from` + `to`. The backend
 * service `resolveRange` raises BadRequestError if either is missing; the
 * UI passes the toggle's selected period and defers `custom` to v7.1
 * (period selector chip renders disabled with "Coming v7.1" tooltip per
 * RESEARCH Open Q1).
 */
import { apiGet } from './client';

// ===== Types =====

export type PortfolioPeriod =
  | 'this_month'
  | 'this_quarter'
  | 'this_year'
  | 'all_time'
  | 'custom';

export type PortfolioStatus =
  | 'On track'
  | 'At risk'
  | 'Off track'
  | 'On hold'
  | 'Done';

export interface PortfolioProjectManager {
  id: number;
  firstName: string;
  lastName: string;
}

// Phase 91 (D-02a) — a `ProjectAssignment`-derived presentation DTO. No
// relationship to the Prisma `Team`/`TeamMember` models or the `teams`
// table — those back an unrelated SLA coverage-billing concept. See
// docs/private/adr/0004-team-model-scope-clarification.md.
export interface PortfolioTeamMember {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  // Quick 260915-i0x — avatar color slot; null falls back to the name hash.
  avatarColorSeq?: number | null;
  // MKS-203 — relative API path to the effective avatar, or null.
  avatarUrl?: string | null;
}

export interface PortfolioRow {
  id: number;
  name: string;
  fullName: string | null;
  customerId: number | null;
  customerName: string | null;
  customerColorHex: string | null;
  projectManager: PortfolioProjectManager | null;
  status: PortfolioStatus;
  billable: boolean;
  hoursLogged: number;
  hoursBudget: number | null;
  burnPct: number;
  /** CZK — the billing ledger is CZK-denominated (docs/private/adr/0003). */
  revenue: number;
  /**
   * MKS-328 — CZK, Σ `Entry.totalCosts` for the period (real per-entry cost:
   * user/assignment cost + overhead). Not rendered on its own; it is what
   * lets PortfolioTable roll a revenue-weighted margin up per group.
   */
  // MKS-332 — omitted entirely for anyone who is not admin or finance, so
  // `undefined` (key absent) is distinct from `margin: null` (earned nothing).
  cost?: number;
  margin?: number | null;
  dueDays: number | null;
  team: PortfolioTeamMember[];
  milestoneLabel: string | null;
  isAttention: boolean;
  /**
   * Phase 50 Plan 50-08 — per-project MIN(orderIndex) across open SLA tasks.
   * Used by group-by 'tier' in PortfolioTable (MO-MULTI-01 single-group rule).
   * Null when the project has no open non-terminal SLA tasks.
   */
  minOpenTierId: number | null;
  minOpenTierOrderIndex: number | null;
  minOpenTierName: string | null;
}

export interface PortfolioKpis {
  activeProjects: {
    value: number;
    sub: string;
    delta: number | null;
    deltaTone: 'good' | 'bad' | 'neutral';
  };
  hoursLogged: {
    value: number;
    budget: number | null;
    pct: number | null;
    delta: number | null;
  };
  revenue: {
    value: number;
    billableHours: number;
    delta: number | null;
  };
  /** MKS-332 — the whole tile is absent for anyone who is not admin or finance. */
  avgMargin?: {
    value: number | null;
    delta: number | null;
  };
}

export interface PortfolioPeriodInfo {
  from: string;
  to: string;
  label: string;
}

/**
 * Phase 50 Plan 07 — Tier rollup row returned by portfolioOverviewService
 * (added in Plan 50-03). One entry per active SLA tier with open tasks.
 */
export interface TierRollupRow {
  tierId: number;
  orderIndex: number;
  name: string;
  taskCount: number;
  breachCount: number;
}

export interface PortfolioResponse {
  kpis: PortfolioKpis;
  needsAttention: PortfolioRow[];
  projects: PortfolioRow[];
  period: PortfolioPeriodInfo;
  /** Per-tier task + breach counts (Plan 50-03). Empty when no SLA tasks exist. */
  tierRollup: TierRollupRow[];
}

// ===== API =====

export async function fetchPortfolioOverview(
  period: PortfolioPeriod,
  from?: Date | string,
  to?: Date | string,
): Promise<PortfolioResponse> {
  const params = new URLSearchParams();
  params.set('period', period);
  if (period === 'custom') {
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
  }
  const endpoint = `/manager/portfolio?${params.toString()}`;
  const res = await apiGet<{ data: PortfolioResponse }>(endpoint);
  return res.data;
}
